/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaSolutionProductMasterService } from '../../../../../service/mediaSolutionProductMaster';
import SolutionProductDefinitionForm from '../../../../common/commonComponents/mediaSolutionProductDefinitionForm';
import { MediaPowderProductMasterService } from '../../../../../service/mediaPowderProductMaster';

interface AddSolutionProductProps {
  onSave: () => void;
  onCancel: () => void;
  showToastMessage: (message: string, type: string) => void;
}

const AddSolutionProduct: React.FC<AddSolutionProductProps> = ({
  onSave,
  onCancel,
  showToastMessage,
}) => {

  const { t } = useTranslation();

  const [mediaSolutions, setMediaSolutions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    selectedMediaSolution: "",
    productName: "",
    categoryId: "",
    manufacturerId: "",
    catalogNumber: "",
    shelfLife: "",
  });

  interface Errors {
    selectedMediaSolution?: string;
    productName?: string;
    categoryId?: string;
    manufacturerId?: string;
    catalogNumber?: string;
    shelfLife?: string;
  }

  const [errors, setErrors] = useState<Errors>({});

  // VALIDATION
  const validateForm = () => {
    const newErrors: Errors = {};
    const shelfLife = parseInt(formData.shelfLife, 10);

    if (!formData.selectedMediaSolution) {newErrors.selectedMediaSolution =t("mediaSolutionProductMaster.errors.mediaSolution");}
    if (!formData.productName) {newErrors.productName =t("mediaSolutionProductMaster.errors.productName");}
    if (!formData.categoryId) {newErrors.categoryId =t("mediaSolutionProductMaster.errors.category");}
    if (!formData.manufacturerId) {newErrors.manufacturerId =t("mediaSolutionProductMaster.errors.manufacturer");}
    if (!formData.catalogNumber) {newErrors.catalogNumber =t("mediaSolutionProductMaster.errors.catalogNumber");}
    if (!formData.shelfLife) {newErrors.shelfLife =t("mediaSolutionProductMaster.errors.shelfLife");}
    if (
        formData.shelfLife &&
        (isNaN(shelfLife) || shelfLife <= 0)
      ) {
        newErrors.shelfLife = "Shelf life must be greater than 0";
    }
    if (Object.keys(newErrors).length > 0) {setErrors(newErrors);return false;}

    setErrors({});
    return true;
  };

  // HANDLE CHANGE
  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // GET MEDIA SOLUTIONS
  const getMediaSolutions = async () => {
    try {
      const response =
        await MediaSolutionProductMasterService.getMediaSolutions();

      if (response?.status && response?.statusCode === 200) {
        setMediaSolutions(response.data);
      }
    } catch (error) {
      console.error("Media Solution API error:", error);
    }
  };

  // GET CATEGORIES
  const getCategories = async () => {
    try {
      const response =
        await MediaSolutionProductMasterService.getCategoryList();

      if (response?.status && response?.statusCode === 200) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error("Category API error:", error);
    }
  };

  // GET MANUFACTURERS
  const getManufacturers = async () => {
    try {
      const response =
        await MediaPowderProductMasterService.getSuppliersByMedia();

      if (response?.status && response?.statusCode === 200) {
        setManufacturers(response.data);
      }
    } catch (error) {
      console.error("Manufacturer API error:", error);
    }
  };

  // SAVE
  const handleSave = async () => {

    if (isSaving) return;

    if (!validateForm()) return;

    const payload = {
      solution_master_id: formData.selectedMediaSolution,
      solution_product_name: formData.productName,
      category_id: formData.categoryId,
      manufacturer_id: formData.manufacturerId,
      catalog_number: formData.catalogNumber,
      shelf_life_days: formData.shelfLife,
      interface_name: "Media Solution Product Master",
    };

    setIsSaving(true);

    try {

      const response =
        await MediaSolutionProductMasterService.createSolutionProductDefinition(
          payload
        );

      if (response?.status) {
        showToastMessage(response?.message, "success");
        onSave();
      } else {
        showToastMessage(response?.message, "error");
      }

    } catch (error) {

      showToastMessage(
        t("mediaSolutionProductMaster.errors.errorSaving"),
        "error"
      );

    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    getMediaSolutions();
    getCategories();
    getManufacturers();
  }, []);

  return (
    <SolutionProductDefinitionForm
      mediaSolutions={mediaSolutions}
      categories={categories}
      manufacturers={manufacturers}
      formData={formData}
      errors={errors}
      onChange={handleChange}
      onSave={handleSave}
      onCancel={onCancel}
      isSaving={isSaving}
    />
  );
};

export default AddSolutionProduct;